import type { TimelineActivityRecurrence } from '../services/notification.service';
import {
  getOccurrenceDurationEntry,
  getOccurrenceDurationMinutes,
  warnOccurrenceDurationOnce
} from './occurrence-duration';

/**
 * Window of an occurrence: the occurrence is open from `start` (included)
 * until `end` (excluded)
 */
export interface OccurrenceWindow {
  start: Date;
  end: Date;
}

type OccurrenceWindowRecurrence = Partial<
  Pick<
    TimelineActivityRecurrence,
    | 'id'
    | 'valid_dates'
    | 'rrule_minute_duration'
    | 'rrule_minute_durations'
    | 'is_minute_duration_symmetric'
  >
>;

const MINUTE_MS = 60 * 1000;

/**
 * True when each duration applies before and after the time (SMART-5734)
 */
export function isMinuteDurationSymmetric(
  rrule:
    | Pick<OccurrenceWindowRecurrence, 'is_minute_duration_symmetric'>
    | null
    | undefined
): boolean {
  return Number(rrule?.is_minute_duration_symmetric) === 1;
}

/**
 * Window of the occurrence at `index` (position in `valid_dates`).
 *
 * Without the ± option (unchanged): time -> time + duration, the app closes at
 * the start of the last minute (never later than the server).
 *
 * With the ± option (SMART-5734): time - d -> time + d, kept within the day of
 * the time (00:00 / 23:59). Like the server, the end minute is included.
 * Minute arithmetic on the device-local time, as the other pages.
 * A missing or 0 duration never opens before the time.
 *
 * Returns `undefined` when the occurrence has no date.
 */
export function getOccurrenceWindow(
  rrule: OccurrenceWindowRecurrence | null | undefined,
  index: number
): OccurrenceWindow | undefined {
  const occurrenceDate = rrule?.valid_dates?.[index]?.[0];
  if (!occurrenceDate) return undefined;

  if (isMinuteDurationSymmetric(rrule)) {
    const symmetricWindow = getSymmetricOccurrenceWindow(
      rrule,
      index,
      occurrenceDate
    );
    if (symmetricWindow) return symmetricWindow;
  }

  // Default 1 min if not set
  const start = new Date(occurrenceDate);
  const durationMinutes = getOccurrenceDurationMinutes(rrule, index) ?? 1;
  return {
    start,
    end: new Date(start.getTime() + durationMinutes * MINUTE_MS)
  };
}

/**
 * Positions of `valid_dates` ordered by time, so occurrences scheduled before
 * the reference time (negative minutes, SMART-5735) are handled in time order
 * even if they are not sent in that order.
 * The order is total: unreadable times come after the readable ones, and equal
 * times keep their position.
 */
export function getOccurrenceIndexesInTimeOrder(
  times: Array<Date | undefined>
): number[] {
  return times
    .map((time, index) => ({ index, time: time?.getTime() ?? NaN }))
    .sort((a, b) => {
      const isAReadable = !Number.isNaN(a.time);
      const isBReadable = !Number.isNaN(b.time);
      if (isAReadable !== isBReadable) return isAReadable ? -1 : 1;
      if (!isAReadable) return a.index - b.index;
      return a.time - b.time || a.index - b.index;
    })
    .map(item => item.index);
}

/**
 * Current occurrence of the tile and the occurrence list:
 *   1. among the unsaved windows open now, the one that ends first
 *   2. otherwise the next unsaved window to open
 *   3. ties: earlier scheduled time first, then position in `valid_dates`
 * `isOpen` tells whether its window is open now. Returns `undefined` when no
 * unsaved window is open or still to open.
 */
export function getCurrentOccurrence(
  rrule: OccurrenceWindowRecurrence | null | undefined,
  windows: Array<OccurrenceWindow | undefined>,
  now: Date
): { index: number; isOpen: boolean } | undefined {
  const nowMs = now.getTime();
  const candidates = (rrule?.valid_dates ?? [])
    .map(([date, status], index) => ({
      index,
      time: date ? new Date(date).getTime() : NaN,
      start: windows[index]?.start.getTime() ?? NaN,
      end: windows[index]?.end.getTime() ?? NaN,
      isSaved: status === 'saved'
    }))
    .filter(
      candidate =>
        !candidate.isSaved &&
        !Number.isNaN(candidate.start) &&
        !Number.isNaN(candidate.end)
    );

  type Candidate = (typeof candidates)[number];
  const byTimeThenPosition = (a: Candidate, b: Candidate) =>
    (Number.isNaN(a.time) || Number.isNaN(b.time) ? 0 : a.time - b.time) ||
    a.index - b.index;

  const open = candidates
    .filter(candidate => candidate.start <= nowMs && nowMs < candidate.end)
    .sort((a, b) => a.end - b.end || byTimeThenPosition(a, b));
  if (open.length > 0) return { index: open[0].index, isOpen: true };

  const upcoming = candidates
    .filter(candidate => nowMs < candidate.start)
    .sort((a, b) => a.start - b.start || byTimeThenPosition(a, b));
  if (upcoming.length > 0) return { index: upcoming[0].index, isOpen: false };

  return undefined;
}

/**
 * Minutes applied before and after the time on a ± page: the configured d the
 * window uses (0 for a missing or invalid duration), shown in the
 * "± d minutes" labels. `undefined` on a page without the option.
 * Near the start or end of the day the window is cut at 00:00 / 23:59, and the
 * label keeps the configured ± d, as the back office shows it.
 */
export function getSymmetricDurationMinutes(
  rrule: OccurrenceWindowRecurrence | null | undefined,
  index: number
): number | undefined {
  if (!isMinuteDurationSymmetric(rrule)) return undefined;
  return getRawDurationMinutes(rrule, index);
}

/**
 * Translation key of a "± d minutes" label: undefined when there is no ±
 * label (page without the option, or ±0), the singular key for 1 minute and
 * the plural key above
 */
export function getSymmetricLabelKey(
  minutes: number | undefined,
  singularKey: string,
  pluralKey: string
): string | undefined {
  if (!minutes) return undefined;
  return minutes === 1 ? singularKey : pluralKey;
}

function getSymmetricOccurrenceWindow(
  rrule: OccurrenceWindowRecurrence | null | undefined,
  index: number,
  occurrenceDate: string
): OccurrenceWindow | undefined {
  const time = new Date(occurrenceDate);
  if (isNaN(time.getTime())) {
    console.warn(
      `Invalid date format "${occurrenceDate}" for occurrence ${index} of page ${rrule?.id}, ± option not applied`
    );
    return undefined;
  }

  const durationMinutes = getRawDurationMinutes(rrule, index);

  // Day of the time (00:00 -> 00:00 of the next day)
  const dayStart = new Date(time);
  dayStart.setHours(0, 0, 0, 0);
  const nextDayStart = new Date(dayStart);
  nextDayStart.setDate(nextDayStart.getDate() + 1);

  // time - d -> time + d, kept within the day of the time (00:00 / 23:59)
  return {
    start: new Date(
      Math.max(time.getTime() - durationMinutes * MINUTE_MS, dayStart.getTime())
    ),
    // The end minute is included, as on the server
    end: new Date(
      Math.min(
        time.getTime() + (durationMinutes + 1) * MINUTE_MS,
        nextDayStart.getTime()
      )
    )
  };
}

/**
 * Duration of the occurrence without the 1-minute minimum, so a missing or 0
 * duration gives the same ±0 window as the server (the minute of the time)
 */
function getRawDurationMinutes(
  rrule: OccurrenceWindowRecurrence | null | undefined,
  index: number
): number {
  const entry = getOccurrenceDurationEntry(rrule, index);
  let rawDuration: unknown;

  if (entry.hasList) {
    rawDuration = entry.value;
    if (rawDuration === undefined || rawDuration === null) return 0;
  } else {
    rawDuration = parseInt(String(rrule?.rrule_minute_duration ?? ''), 10);
    if (isNaN(rawDuration as number)) return 0;
  }

  const duration = Math.trunc(Number(rawDuration));
  if (!Number.isFinite(duration) || duration < 0) {
    warnOccurrenceDurationOnce(
      `${rrule?.id}|${index}|${String(rawDuration)}|±`,
      `Invalid minute duration "${rawDuration}" for occurrence ${index} of page ${rrule?.id}, using 0 before and after the time`
    );
    return 0;
  }
  return duration;
}
